import { apiClient } from '../apiClient';

/**
 * Worker & Position History (phase 5 read APIs).
 * See docs/WORKER_POSITION_HISTORY_PLAN.md.
 */

export interface EmploymentSpan {
  employment_id: string | null;
  legal_entity_id: string | null;
  start_date: string | null;
  end_date: string | null;
  worker_type: string | null;
  employment_category: string | null;
  grade_level: string | null;
  nqf_level: string | null;
  campus: string | null;
  faculty: string | null;
  is_current: boolean;
}

export interface AssignmentSpan {
  fo_position_id: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_primary: boolean;
  reason_code: string | null;
  is_current: boolean;
}

export interface WorkerChange {
  attribute: string;
  from_value: string | null;
  to_value: string | null;
  effective_date: string | null;
  observed_at: string | null;
}

export interface WorkHistory {
  employee_id: string;
  display_name: string;
  current: {
    position_type: string | null;
    employment_category: string | null;
    worker_status: string | null;
    termination_date: string | null;
  };
  employments: EmploymentSpan[];
  assignments: AssignmentSpan[];
  changes: WorkerChange[];
}

export interface EmploymentTypeHistory {
  employee_id: string;
  current_position_type: string | null;
  current_category: string | null;
  transitions: { from: string | null; to: string | null; effective_date: string | null; observed_at: string | null }[];
}

export interface PositionIncumbent {
  personnel_number: string;
  user_id: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_primary: boolean;
  reason_code: string | null;
  is_current: boolean;
}

export interface PositionHistory {
  fo_position_id: string;
  title: string | null;
  status: string | null;
  currently_vacant: boolean | null;
  incumbents: PositionIncumbent[];
  vacancy_periods: { from: string | null; to: string | null; days: number }[];
}

const U = '/api/v1/identity/users';
const ORG = '/api/v1/identity/org';

export const workerHistoryService = {
  /** A worker's full work-history timeline (employments + seat journey + change-log). */
  async getWorkHistory(employeeId: string): Promise<WorkHistory> {
    const r = await apiClient.get<WorkHistory>(`${U}/${employeeId}/work-history`);
    return r.data;
  },

  /** A worker's employment-type transition series (FTC<->ICA<->PERM) + current type. */
  async getEmploymentTypeHistory(employeeId: string): Promise<EmploymentTypeHistory> {
    const r = await apiClient.get<EmploymentTypeHistory>(`${U}/${employeeId}/employment-type-history`);
    return r.data;
  },

  /** A seat's incumbent timeline + computed vacancy periods. */
  async getPositionHistory(foPositionId: string): Promise<PositionHistory> {
    const r = await apiClient.get<PositionHistory>(`${ORG}/positions/${encodeURIComponent(foPositionId)}/history`);
    return r.data;
  },
};

export default workerHistoryService;
